// Selection visuals for the MDS perceptual map.
// Port of BrandPerceptialMapping.Mds.ChartView#render selection block (bpm/mds/chart.coffee
// L561-792), excluding the mouseup/hover/drag handlers (those are phase 5).
// Re-styles the already-drawn static scene — never rebuilds geometry.

import * as d3 from 'd3'

import { CLS, ID } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsClasses'

// bubbleRadius(p): petal arc radius by association strength
const bubbleRadius = (p = 1) => (p < 0.2 ? 5 : p < 0.3 ? 10 / 1.5 : 10)

// Petal arc path from the centre out along an axis, scaled by mean association
const buildPetalPath = (point, axis, { axises, meanList, scoresMax, projection, center }) => {
  const maxLength = axises[0].l
  const meanLength = (meanList?.[point.val]?.[axis.val] || 0) / scoresMax
  const r = bubbleRadius(axis.l / maxLength)
  const px = projection.scaleX(axis.x * meanLength)
  const py = projection.scaleY(axis.y * meanLength)

  const x1 = Math.cos(Math.PI / 2 + axis.a) * r + px
  const y1 = Math.sin(Math.PI / 2 + axis.a) * -r + py
  const x2 = Math.cos((3 * Math.PI) / 2 + axis.a) * r + px
  const y2 = Math.sin((3 * Math.PI) / 2 + axis.a) * -r + py

  const cx = projection.scaleX(center.xm)
  const cy = projection.scaleY(center.ym)
  return `M ${cx} ${cy} L ${x2} ${y2} A ${r} ${r} 1 1 0 ${x1} ${y1} L ${cx} ${cy}`
}

// Single-brand petals: one gradient-filled arc per axis, opacity gated by attribute selection
const drawPetals = (selectedGroup, ctx) => {
  const {
    point,
    axises,
    answer,
    projection,
    center,
    questionId,
    mdsAttributesValue,
    colorGradient,
    scoresMax,
    meanList
  } = ctx

  selectedGroup.select(`g.${CLS.bublik} circle.${CLS.centerOuter}`).attr('opacity', 0.2)

  const maxLength = axises[0].l
  axises.forEach(axis => {
    const g = selectedGroup.append('g').classed(CLS.selected, true)
    const meanLength = (meanList?.[point.val]?.[axis.val] || 0) / scoresMax
    const r = bubbleRadius(axis.l / maxLength)
    const gradId = ID.gradient(questionId, point.id, axis.x, axis.y)

    const grad = g
      .append('defs')
      .append('linearGradient')
      .attr('id', gradId)
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', projection.scaleX(center.xm))
      .attr('y1', projection.scaleY(center.ym))
      .attr('x2', r + projection.scaleX(axis.x * meanLength))
      .attr('y2', -r + projection.scaleY(axis.y * meanLength))

    const color = colorGradient(
      answer.meanStat?.attr?.min?.[axis.val],
      answer.meanStat?.attr?.max?.[axis.val],
      answer.meanList?.[point.val]?.[axis.val]
    )
    grad.append('stop').attr('offset', '35%').attr('stop-color', color).attr('stop-opacity', 0.8)
    grad.append('stop').attr('offset', '99.9%').attr('stop-color', color).attr('stop-opacity', 0.3)

    g.append('path')
      .classed(CLS.selected, true)
      .attr('d', buildPetalPath(point, axis, { axises, meanList, scoresMax, projection, center }))
      .attr('fill', `url(#${gradId})`)
      .attr('opacity', mdsAttributesValue.includes(axis.id) ? 1 : 0)
  })
}

/**
 * Highlight axis vectors + labels by selected attributes.
 * @param {{ axises:Array, mdsAttributesValue:string[], questionId, selectedCount:number }} params
 */
export const applyAxisHighlight = (svgNode, { axises, mdsAttributesValue, questionId, selectedCount }) => {
  const origin = `${window.location.origin}${window.location.pathname}`

  d3.select(svgNode)
    .select(`g.${CLS.lines}`)
    .selectAll(`g.${CLS.axisLines}`)
    .each(function (_, i) {
      const axis = axises[i]
      if (!axis) return
      const contains = mdsAttributesValue.includes(axis.id)
      const node = d3.select(this)
      const markerId = contains ? ID.arrowDark(questionId) : ID.arrowGray(questionId)

      node
        .select(`line.${CLS.lines}`)
        .attr('stroke', contains ? '#333333' : '#CCCCCC')
        .attr('stroke-width', axis.index < 3 ? '1.5px' : '.5px')
        .attr('marker-end', `url(${origin}#${markerId})`)
      node.select(`circle.${CLS.endInner}`).attr('fill', contains ? '#6CACE1' : '#CCCCCC')
      node
        .select(`circle.${CLS.endOuter}`)
        .attr('opacity', selectedCount === 1 ? 0.8 : 1)
        .transition()
        .duration(1000)
        .attr('r', contains ? 10 : 3)
    })

  // Axis-label tspans carry data-id from updateAxisLabels — recolour by selection
  svgNode.querySelectorAll(`.${CLS.axisLabel}`).forEach(label => {
    label.querySelectorAll('tspan').forEach(span => {
      const contains = mdsAttributesValue.includes(String(span.dataset.id))
      span.style.fill = contains ? '#000000' : '#EEEEEE'
    })
  })
}

/**
 * Apply brand selection: petals for a single brand, point/label visibility otherwise.
 * @param {{ axises, points, projection, center, answer, mdsBrandsValue, mdsAttributesValue,
 *   questionId, colorGradient }} params
 */
export const applySelection = (svgNode, params) => {
  const { axises, points, projection, center, answer, mdsBrandsValue, mdsAttributesValue, questionId, colorGradient } =
    params

  const root = d3.select(svgNode)
  const selectedGroup = root.select(`g.${CLS.selected}`)
  const pointsGroup = root.select(`g.${CLS.points}`)
  const selected = mdsBrandsValue || []
  const meanList = answer?.meanList
  const scoresMax = answer?.scores?.max

  // Clear previous petals before redrawing
  selectedGroup.selectAll(`g.${CLS.selected}`).remove()

  if (!selected.length) {
    selectedGroup.select(`g.${CLS.bublik} circle.${CLS.centerOuter}`).attr('opacity', 0.8)
    pointsGroup.selectAll(`g.${CLS.point}`).each(function () {
      const node = d3.select(this)
      node.select(`circle.${CLS.outer}`).attr('r', 0).style('opacity', 0)
      node.select(`circle.${CLS.inner}`).attr('fill', '#000000')
      node.select(`g.${CLS.label}`).attr('opacity', 0)
      node.select(`path.${CLS.pointPath}`).attr('opacity', 0)
      node.select('rect').remove()
    })
    return
  }

  const selectedSingle = selected.length === 1 ? selected[0] : null

  if (selectedSingle != null) {
    const point = points.find(p => p.id === selectedSingle)
    if (point) {
      drawPetals(selectedGroup, {
        point,
        axises,
        answer,
        projection,
        center,
        questionId,
        mdsAttributesValue,
        colorGradient,
        scoresMax,
        meanList
      })
    }
  }

  pointsGroup.selectAll(`g.${CLS.point}`).each(function (_, i) {
    const point = points[i]
    if (!point) return
    const contains = selected.includes(point.id)
    const isSingle = point.id === selectedSingle
    const node = d3.select(this)

    node
      .select(`circle.${CLS.outer}`)
      .attr('r', isSingle ? 10 : contains ? 6 : 0)
      .attr('opacity', contains ? 1 : 0)
    node.select(`circle.${CLS.inner}`).attr('fill', contains ? '#000000' : '#666666')
    node.select(`g.${CLS.label}`).attr('opacity', isSingle || contains ? 1 : 0)
    node.select(`path.${CLS.pointPath}`).attr('opacity', isSingle || contains ? 1 : 0)
  })
}
